import { cloneDeep } from 'lodash';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import { isMenuByProduct } from '../libraries/Utils';
import { calculateVat } from '../vatRates/VatRatesMgr';
import { evaluateProductDiscount } from './OrderRuleEvaluator';
import { ensureOrderBuildSession } from './OrderBuildSession';
import { DiscountScope } from '../plugins/commonTypes/ProductTypes';
const MY_GENERATION = DomainCoreGeneration.Generation5;
const getIdAsName = (guid, prefix) => {
    return prefix + guid.substring(guid.length - 8).replace(/0/g, '');
};
const createInitialState = () => ({
    isMenuProduct: false,
    discountScopes: [DiscountScope.All],
});
const createOrderLineFromGroup = (group) => {
    const orderLine = {
        name: group.name || group.options.description || getIdAsName(group.id, 'Group'),
        type: 'Aggregation',
        options: group.options ? cloneDeep(group.options) : {},
        subLines: [],
        generation: MY_GENERATION,
        _source: { entity: 'group', id: group.id },
    };
    return orderLine;
};
const createOrderLineFromProduct = (context, args) => {
    var _a, _b, _c, _d;
    const { productCatalog, options, state, session } = context;
    const { product, quantity, depth, rootProductId, productSelectionNode, groupOptions } = args;
    const { productSelectionId, category = null, rcmSource = null, } = productSelectionNode;
    if (depth === 0) {
        const rootMenuOverride = (_a = session.isRootProductMenu) !== null && _a !== void 0 ? _a : options.isRootProductMenu;
        state.isMenuProduct = rootMenuOverride === undefined ? isMenuByProduct(product) : rootMenuOverride;
        state.discountScopes = state.isMenuProduct
            ? [DiscountScope.Group, DiscountScope.All]
            : [DiscountScope.Single, DiscountScope.All];
    }
    const rootProduct = state.isMenuProduct ? productCatalog.getProduct(rootProductId, true) : null;
    const groupContext = groupOptions === null || groupOptions === void 0 ? void 0 : groupOptions.context;
    const computeOrderLinePrice = () => {
        var _a, _b, _c;
        if (groupContext) {
            const groupContextPrice = (_a = product.pricesMap) === null || _a === void 0 ? void 0 : _a[groupContext];
            if (groupContextPrice) {
                return depth > 0 && ((_b = rootProduct === null || rootProduct === void 0 ? void 0 : rootProduct.options) === null || _b === void 0 ? void 0 : _b.isMiniMenu) ? product.price : groupContextPrice;
            }
        }
        if (product.priceStructure && product.priceStructure.priceInGroup && depth > 0) {
            if ((_c = rootProduct === null || rootProduct === void 0 ? void 0 : rootProduct.options) === null || _c === void 0 ? void 0 : _c.isMiniMenu) {
                return product.priceStructure.defaultPrice;
            }
            return product.priceStructure.priceInGroup;
        }
        if (product.price) {
            return product.price;
        }
        return undefined;
    };
    const orderLinePrice = computeOrderLinePrice();
    const orderLineQuantity = quantity;
    const totalPrice = orderLinePrice ? orderLinePrice * ((_b = orderLineQuantity) !== null && _b !== void 0 ? _b : 1) : 0;
    const totalVat = calculateVat(product.vatRatePerMille, totalPrice);
    const discount = evaluateProductDiscount({
        product,
        productSelectionNode,
        quantity: orderLineQuantity,
        options,
        productCatalog,
        discountScopes: state.discountScopes,
        groupContext,
        session,
    });
    const onlinePos = (_c = product.onlinePosMap) === null || _c === void 0 ? void 0 : _c[groupContext];
    const groupContextPriceElement = groupContext ? (_d = product.pricesMap) === null || _d === void 0 ? void 0 : _d[groupContext] : undefined;
    const orderLinePriceStructure = groupContextPriceElement !== undefined && groupContextPriceElement !== null
        ? Object.assign(Object.assign({}, product.priceStructure), { priceInGroup: groupContextPriceElement }) : product.priceStructure;
    const orderLine = Object.assign(Object.assign(Object.assign({ isMenu: state.isMenuProduct, category: (category !== null && category !== void 0 ? category : null), type: 'Product', productId: product.id, name: product.name || getIdAsName(product.id, 'product'), subLines: [], quantity: orderLineQuantity, aggregatedQuantity: undefined, options: Object.assign(Object.assign({}, product.options), { foreignIds: (onlinePos === null || onlinePos === void 0 ? void 0 : onlinePos.foreignIds) || product.foreignIds, foreignNames: (onlinePos === null || onlinePos === void 0 ? void 0 : onlinePos.foreignNames) || product.foreignNames, overrides: product.overrides, priceStructure: orderLinePriceStructure }), unitType: (product.unit || undefined), accounting: product.options.accounting || undefined, discount: (discount || undefined), applicableDiscounts: product.applicableDiscounts, price: orderLinePrice, totalPrice,
        rootProductId, totalPriceAccum: undefined, generation: MY_GENERATION, productSelectionId: productSelectionId, vatRatePerMille: product.vatRatePerMille, totalVat }, (product.vatCategoryId ? { vatCategoryId: product.vatCategoryId } : {})), (product.vatCategoryName ? { vatCategoryName: product.vatCategoryName } : {})), { rcmSource: rcmSource !== null && rcmSource !== void 0 ? rcmSource : undefined });
    if (!orderLine.accounting)
        delete orderLine.accounting;
    if (!orderLine.discount)
        delete orderLine.discount;
    return orderLine;
};
const createOrderLineFromConfig = (config, configSelectionNode) => {
    const configOptions = config.options.options;
    const selectedValue = Array.isArray(configSelectionNode.selectedValue)
        ? configSelectionNode.selectedValue
        : configSelectionNode.selectedValue !== undefined
            ? [configSelectionNode.selectedValue]
            : [];
    const orderLine = {
        name: config.name || getIdAsName(config.id, 'config'),
        type: 'Config',
        options: config.options,
        selectedOptions: configOptions
            .filter((opt, index) => selectedValue.indexOf(index) !== -1)
            .map((option) => new Object(Object.assign({}, option))),
        unselectedOptions: configOptions.reduce((acc, option, index) => {
            if (selectedValue.indexOf(index) === -1) {
                acc.push(new Object(Object.assign(Object.assign({}, option), { optionIndex: index, configId: config.id })));
            }
            return acc;
        }, []),
        subLines: [],
        generation: MY_GENERATION,
    };
    return orderLine;
};
const mapIncludedInMenuPrice = (productCatalog, rootProductId, product) => {
    const { groups } = product;
    if (!groups || groups.length === 0) {
        return {};
    }
    return groups.reduce((dict, group) => {
        const defaultSelectedProductId = group.options.defaultSelected;
        const defaultSelectedProduct = group.products.find((groupProduct) => groupProduct.id === defaultSelectedProductId) || group.products[0];
        const rootProduct = productCatalog.getProduct(rootProductId, true);
        const includedInMenuPrice = defaultSelectedProduct.priceStructure &&
            defaultSelectedProduct.priceStructure.priceInGroup &&
            !rootProduct.options.isMiniMenu
            ? defaultSelectedProduct.priceStructure.priceInGroup
            : defaultSelectedProduct.price;
        dict[group.id] = includedInMenuPrice;
        return dict;
    }, {});
};
const recurse = (context, orderLineArray, productSelectionNode, depth, rootProductId, groupOptions) => {
    const product = context.productCatalog.getProduct(productSelectionNode.productId, true);
    if (product.options.isNoThanks) {
        return;
    }
    const includedInMenuPrices = mapIncludedInMenuPrice(context.productCatalog, rootProductId, product);
    const effectiveQuantity = productSelectionNode.quantity;
    const orderLine = createOrderLineFromProduct(context, {
        product,
        quantity: effectiveQuantity,
        depth,
        rootProductId,
        productSelectionNode,
        groupOptions,
    });
    if (orderLine.type === 'Product' && productSelectionNode.quantity === undefined) {
        orderLine.quantity = 1;
    }
    orderLine.subLines = [];
    if (productSelectionNode.configurations) {
        productSelectionNode.configurations.forEach((configSelectionNode) => {
            const config = product.getConfig(configSelectionNode.configurationId);
            const configLine = createOrderLineFromConfig(config, configSelectionNode);
            if (orderLine.type === 'Product') {
                orderLine.subLines.push(configLine);
            }
        });
    }
    if (productSelectionNode.groups) {
        productSelectionNode.groups.forEach((groupRef) => {
            var _a, _b, _c, _d;
            const group = product.getGroup(groupRef.groupId);
            const groupLine = createOrderLineFromGroup(group);
            if ((_a = groupLine.options) === null || _a === void 0 ? void 0 : _a.includedInMenuPrice) {
                const includedPrice = includedInMenuPrices[(_b = groupLine._source) === null || _b === void 0 ? void 0 : _b.id];
                if (Number.isFinite(includedPrice)) {
                    groupLine.options.includedInMenuPrice = includedPrice;
                }
                else {
                    delete groupLine.options.includedInMenuPrice;
                }
            }
            groupLine.subLines = [];
            groupRef.selectedProducts.forEach((selected) => {
                const innerProductSelectionNode = typeof selected === 'string'
                    ? { productId: selected }
                    : selected;
                recurse(context, groupLine.subLines, innerProductSelectionNode, depth + 1, rootProductId, group.options);
            });
            groupLine.generation = MY_GENERATION;
            if (groupLine.options.defaultSelected) {
                const defaultSelectedProduct = context.productCatalog.getProduct(groupLine.options.defaultSelected, true);
                if (defaultSelectedProduct) {
                    const groupContext = (_c = groupLine.options) === null || _c === void 0 ? void 0 : _c.context;
                    const groupContextPriceElement = groupContext ? (_d = defaultSelectedProduct.pricesMap) === null || _d === void 0 ? void 0 : _d[groupContext] : undefined;
                    const defaultSelectedPriceStructure = groupContextPriceElement !== undefined && groupContextPriceElement !== null
                        ? Object.assign(Object.assign({}, defaultSelectedProduct.priceStructure), { priceInGroup: groupContextPriceElement }) : defaultSelectedProduct.priceStructure;
                    groupLine.options.defaultSelectedData = {
                        priceStructure: defaultSelectedPriceStructure,
                    };
                }
            }
            orderLine.subLines.push(groupLine);
        });
    }
    orderLine.generation = MY_GENERATION;
    orderLineArray.push(orderLine);
};
export const buildOrderLineTree = ({ productCatalog, productSelection, options, session, }) => {
    const orderLines = [];
    const sessionRef = ensureOrderBuildSession(session, {
        cartProductsMap: options === null || options === void 0 ? void 0 : options.cartProductsMap,
        exceededPriceRules: options === null || options === void 0 ? void 0 : options.exceededPriceRules,
        isRootProductMenu: options === null || options === void 0 ? void 0 : options.isRootProductMenu,
    });
    productSelection.forEach((selectionNode) => {
        const state = createInitialState();
        const context = {
            productCatalog,
            options,
            state,
            session: sessionRef,
        };
        recurse(context, orderLines, selectionNode, 0, selectionNode.productId, {});
    });
    return orderLines;
};
export const OrderLineTreeBuilder = Object.freeze({
    buildOrderLineTree,
});
